import {
    FeatureTreeNodeKind,
    type EditSketchRequest,
    type FeatureTreeNodeMeta,
} from './feature-tree-types';

/**
 * 设计树编辑草图请求解析状态。
 */
export type ResolveEditSketchRequestStatus =
    /** 已解析为统一草图编辑请求。 */
    | 'resolved'
    /** 节点不支持编辑草图，调用方应静默忽略。 */
    | 'ignored'
    /** 特征声明支持编辑草图，但缺少来源草图 ID，调用方应提示用户。 */
    | 'missing-source-sketch'
    /** 特征声明了来源草图，但设计树投影缺少对应草图节点。 */
    | 'missing-sketch-node';

/**
 * 设计树编辑草图请求解析结果。
 */
export interface ResolveEditSketchRequestResult {
    /** 解析状态。 */
    status: ResolveEditSketchRequestStatus;
    /** 统一编辑草图请求；仅 status 为 resolved 时存在。 */
    request?: EditSketchRequest;
    /** 应先进入的设计树草图节点 key；用于复用树选中链路。 */
    selectionKey?: string;
}

/**
 * 判断节点是否为指定草图对应的设计树草图节点。
 *
 * 设计说明：
 * 右键编辑草图必须复用“点击设计树草图节点”的既有链路，因此这里只接受真实草图节点，
 * 避免 feature 节点绕开选中链路后造成重建订阅和工具栏状态分叉。
 *
 * @param nodeMeta 节点元数据。
 * @param sketchGroupId 草图组 ID。
 * @returns 是否为目标草图节点。
 */
function isTargetSketchNode(nodeMeta: FeatureTreeNodeMeta | undefined, sketchGroupId: string): boolean {
    return nodeMeta?.kind === FeatureTreeNodeKind.Object
        && nodeMeta.objectType === 'sketch'
        && nodeMeta.objectId === sketchGroupId;
}

/**
 * 解析编辑草图前应选中的设计树草图节点 key。
 *
 * 设计说明：
 * 1. 草图节点入口直接使用触发 key；
 * 2. 特征节点入口优先使用投影层约定的 `${featureId}::${sketchId}` 子节点 key；
 * 3. 若约定 key 不存在，再遍历节点表寻找同一草图，兼容后续投影 key 策略扩展。
 *
 * @param input 解析输入。
 * @returns 目标草图节点 key，无法解析时返回 null。
 */
function resolveSketchSelectionKey(input: {
    /** 触发操作的节点 key。 */
    triggerKey: string;
    /** 来源特征 ID；草图节点入口为空。 */
    featureId?: string;
    /** 目标草图组 ID。 */
    sketchGroupId: string;
    /** 设计树节点映射。 */
    featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>;
}): string | null {
    const {triggerKey, featureId, sketchGroupId, featureTreeNodeMap} = input;
    if (isTargetSketchNode(featureTreeNodeMap.get(triggerKey), sketchGroupId)) {
        return triggerKey;
    }

    const projectedChildKey = featureId ? `${featureId}::${sketchGroupId}` : '';
    if (projectedChildKey && isTargetSketchNode(featureTreeNodeMap.get(projectedChildKey), sketchGroupId)) {
        return projectedChildKey;
    }

    for (const [candidateKey, candidateMeta] of featureTreeNodeMap.entries()) {
        if (isTargetSketchNode(candidateMeta, sketchGroupId)) {
            return candidateKey;
        }
    }

    return null;
}

/**
 * 从设计树节点元数据解析统一编辑草图请求。
 *
 * 设计说明：
 * 1. 草图节点与特征节点最终都只表达“恢复某个草图组编辑”；
 * 2. 特征节点只在动作层解析来源草图，避免 Editor provider 再关心 feature/sketch 双分支；
 * 3. 该函数保持纯解析职责，不读取 Viewer 或 UI 状态，便于测试并降低耦合。
 *
 * @param input 解析输入。
 * @returns 解析结果。
 */
export function resolveEditSketchRequest(input: {
    /** 触发操作的设计树节点 key。 */
    key: string;
    /** 节点元数据。 */
    nodeMeta?: FeatureTreeNodeMeta;
    /** 设计树节点映射，用于解析来源草图的树节点 key。 */
    featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>;
}): ResolveEditSketchRequestResult {
    const {key, nodeMeta, featureTreeNodeMap} = input;
    if (!nodeMeta || !nodeMeta.objectId) {
        return {status: 'ignored'};
    }

    if (nodeMeta.objectType === 'sketch') {
        const selectionKey = resolveSketchSelectionKey({
            triggerKey: key,
            sketchGroupId: nodeMeta.objectId,
            featureTreeNodeMap,
        });
        if (!selectionKey) {
            return {status: 'missing-sketch-node'};
        }
        return {
            status: 'resolved',
            selectionKey,
            request: {
                sketchGroupId: nodeMeta.objectId,
                source: 'sketch-node',
                triggerNodeKey: key,
            },
        };
    }

    if (!nodeMeta.featureKind || !nodeMeta.canEditSketch) {
        return {status: 'ignored'};
    }

    if (!nodeMeta.sourceSketchId) {
        return {status: 'missing-source-sketch'};
    }

    const selectionKey = resolveSketchSelectionKey({
        triggerKey: key,
        featureId: nodeMeta.objectId,
        sketchGroupId: nodeMeta.sourceSketchId,
        featureTreeNodeMap,
    });
    if (!selectionKey) {
        return {status: 'missing-sketch-node'};
    }

    return {
        status: 'resolved',
        selectionKey,
        request: {
            sketchGroupId: nodeMeta.sourceSketchId,
            source: 'feature-node',
            triggerNodeKey: key,
            featureId: nodeMeta.objectId,
            featureKind: nodeMeta.featureKind,
        },
    };
}
